import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminApi } from '../services/adminApi';
import { Loader2, ArrowLeft, Shield, Clock, Zap } from 'lucide-react';

export const AdminUserDetail: React.FC = () => {
    const { uid } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const fetchUser = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi.getUser(uid!);
            setData(res);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => {
        if (uid) fetchUser();
    }, [uid, fetchUser]);

    const handleAction = async (action: string, payload?: any) => {
        if (!window.confirm(`Are you sure you want to ${action}?`)) return;

        setActionLoading(action);
        try {
            await adminApi.setEntitlements(uid!, action, payload);
            await fetchUser(); // Refresh data
        } catch (err) {
            alert('Action failed');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) return <div className="p-10 text-center flex justify-center text-gray-400"><Loader2 className="animate-spin" /></div>;
    if (!data) return <div className="p-10 text-center">User not found</div>;

    const { user, progress } = data;

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 p-8">
            <div className="max-w-4xl mx-auto">
                <Link to="/admin" className="inline-flex items-center text-gray-500 mb-6 hover:text-gray-900">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back to List
                </Link>

                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-2xl font-bold mb-1">{user.email}</h1>
                            <p className="text-gray-500 font-mono text-sm">{user.uid}</p>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-500">Last Active</div>
                            <div className="font-medium">
                                {user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : 'Never'}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 truncate max-w-[200px]" title={user.lastActiveRoute}>
                                {user.lastActiveRoute || '-'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Entitlements Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

                    {/* OSP Status */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h2 className="text-lg font-semibold mb-4 flex items-center">
                            <Zap className="w-5 h-5 mr-2 text-yellow-500" /> OSP AI Tutor
                        </h2>

                        <div className="space-y-3 mb-6">
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">Pro Active Until</span>
                                <span className="font-medium">
                                    {user.ospAiProActiveUntil ? new Date(user.ospAiProActiveUntil).toLocaleDateString() : 'Inactive'}
                                </span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">Trial Used</span>
                                <span className="font-medium">{user.ospUsage?.hasUsedTrial ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">Trial Blocked</span>
                                <span className={`font-medium ${user.trialAbuseBlocked ? 'text-red-600' : 'text-green-600'}`}>
                                    {user.trialAbuseBlocked ? 'BLOCKED' : 'Allowed'}
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                disabled={!!actionLoading}
                                onClick={() => handleAction('grantOspPro', { days: 30 })}
                                className="px-3 py-1.5 bg-green-50 text-green-700 text-sm font-medium rounded hover:bg-green-100 transition-colors"
                            >
                                Grant Pro (30d)
                            </button>
                            <button
                                disabled={!!actionLoading}
                                onClick={() => handleAction('revokeOspPro')}
                                className="px-3 py-1.5 bg-red-50 text-red-700 text-sm font-medium rounded hover:bg-red-100 transition-colors"
                            >
                                Revoke Pro
                            </button>
                            <button
                                disabled={!!actionLoading}
                                onClick={() => handleAction(user.trialAbuseBlocked ? 'unblockTrial' : 'blockTrial')}
                                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors"
                            >
                                {user.trialAbuseBlocked ? 'Unblock Trial' : 'Block Trial'}
                            </button>
                        </div>
                    </div>

                    {/* ZSV / Plus Status */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h2 className="text-lg font-semibold mb-4 flex items-center">
                            <Shield className="w-5 h-5 mr-2 text-blue-500" /> ZSV / Account Role
                        </h2>

                        <div className="space-y-3 mb-6">
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">Role</span>
                                <span className="font-medium uppercase">{user.role || 'free'}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">ZSV Plus Status</span>
                                <span className="font-medium">
                                    {user.entitlements?.zsvPlus ? 'LIFETIME' : 'None'}
                                </span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">ZSV Pro Status</span>
                                <span className="font-medium">
                                    {user.entitlements?.zsvPro ? 'LIFETIME' : 'None'}
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                disabled={!!actionLoading}
                                onClick={() => handleAction('grantZsvPlus')}
                                className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded hover:bg-blue-100 transition-colors"
                            >
                                Grant Plus
                            </button>
                            <button
                                disabled={!!actionLoading}
                                onClick={() => handleAction('grantZsvPro')}
                                className="px-3 py-1.5 bg-purple-50 text-purple-700 text-sm font-medium rounded hover:bg-purple-100 transition-colors"
                            >
                                Grant Pro
                            </button>
                            <button
                                disabled={!!actionLoading}
                                onClick={() => handleAction('revokeZsv')}
                                className="px-3 py-1.5 bg-red-50 text-red-700 text-sm font-medium rounded hover:bg-red-100 transition-colors"
                            >
                                Revoke All ZSV
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* Chat History */}
                    <div className="bg-white rounded-xl shadow-sm border p-6 flex flex-col h-[500px]">
                        <h2 className="text-lg font-semibold mb-4 flex items-center">
                            <Zap className="w-5 h-5 mr-2 text-indigo-500" /> Latest AI Chat Session
                        </h2>
                        {data.chat ? (
                            <div className="flex-1 overflow-y-auto space-y-4 p-4 bg-gray-50 rounded-lg border">
                                {data.chat.messages.map((msg: any, idx: number) => (
                                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] rounded-lg p-3 text-sm 
                                            ${msg.role === 'user'
                                                ? 'bg-blue-500 text-white rounded-br-none'
                                                : 'bg-white border text-gray-800 rounded-bl-none shadow-sm'}`}>
                                            <div className="mb-1">{msg.text}</div>
                                            <div className={`text-[10px] ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                                                {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ''}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-lg border border-dashed">
                                <Zap className="w-8 h-8 mb-2 opacity-20" />
                                <p>No chat history found</p>
                            </div>
                        )}
                        {data.chat && (
                            <div className="mt-2 text-xs text-gray-400 text-center">
                                Chat ID: {data.chat.id}
                            </div>
                        )}
                    </div>

                    {/* Progress & Data */}
                    <div className="space-y-6">
                        {/* Progress */}
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <h3 className="font-semibold mb-4 flex items-center">
                                <Clock className="w-5 h-5 mr-2 text-green-500" />
                                User Progress
                            </h3>
                            {progress ? (
                                <div className="space-y-3">
                                    {Object.entries(progress).map(([key, value]: [string, any]) => (
                                        <div key={key} className="flex justify-between items-center border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                                            <span className="text-gray-600 capitalize text-sm">{key.replace(/_/g, ' ')}</span>
                                            <span className="font-medium text-sm">
                                                {typeof value === 'object' ? JSON.stringify(value).substring(0, 30) + '...' : String(value)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No progress data available.</p>
                            )}
                        </div>

                        {/* Raw User Data */}
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <h3 className="font-semibold mb-2 text-gray-500 text-sm">Raw Metadata</h3>
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">UID</span>
                                    <span className="font-mono">{user.uid}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Created</span>
                                    <span className="font-mono">{user.createdAt ? new Date(user.createdAt).toLocaleString() : '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Last IP</span>
                                    <span className="font-mono text-gray-400">Hidden</span>
                                </div>
                            </div>

                            <details className="mt-4">
                                <summary className="text-xs text-blue-500 cursor-pointer hover:underline">View Full JSON</summary>
                                <pre className="text-[10px] bg-gray-50 p-2 rounded mt-2 overflow-auto max-h-40 border">
                                    {JSON.stringify(user, null, 2)}
                                </pre>
                            </details>
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
};
